import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Heart, HandHeart } from "lucide-react";
import { useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { type MedicalCondition, type ConditionWallet, iconMap, WalletCard } from "./shared";

export function ConditionDetail({ conditionId }: { conditionId: string }) {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  
  const { data: condition, isLoading: conditionLoading } = useQuery<MedicalCondition>({
    queryKey: ['/api/public/conditions', conditionId],
    enabled: !!conditionId,
  });
  
  const { data: allWallets, isLoading: walletsLoading } = useQuery<ConditionWallet[]>({
    queryKey: ['/api/public/condition-wallets'],
  });
  
  const wallets = useMemo(() => {
    if (!allWallets) return [];
    return allWallets.filter(w => w.conditionId === conditionId);
  }, [allWallets, conditionId]);
  
  const Icon = condition?.iconName ? (iconMap[condition.iconName] || Heart) : Heart;
  
  if (conditionLoading || walletsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
          {[1, 2].map(i => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }
  
  if (!condition) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground mb-4">{t('conditions.detail.not-found')}</p>
        <Button variant="outline" onClick={() => setLocation('/conditions')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Conditions
        </Button>
      </Card>
    );
  }
  
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setLocation('/conditions')}
          data-testid="button-back-conditions"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          All Conditions
        </Button>
      </div>
      
      <Card className="p-3 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold mb-2" data-testid="text-condition-name">{condition.name}</h1>
            <p className="text-muted-foreground">{condition.description}</p>
            <Badge variant="outline" className="mt-3">{condition.category}</Badge>
          </div>
        </div>
      </Card>
      
      <section>
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-xl font-semibold">Community Funds ({wallets.length})</h2>
        </div>
        
        {wallets.length === 0 ? (
          <Card className="p-6 sm:p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t('conditions.detail.no-funds-yet')}</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Be the first to help patients with {condition.name}. Community funds provide 
              direct financial support for healthcare costs like treatments, medications, and equipment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="default"
                onClick={() => {
                  const createPath = `/wallet/create?type=condition&conditionId=${conditionId}`;
                  if (user) {
                    setLocation(createPath);
                  } else {
                    setLocation(`/auth?returnTo=${encodeURIComponent(createPath)}`);
                  }
                }}
                data-testid="button-request-fund"
              >
                <HandHeart className="w-4 h-4 mr-2" />
                Request a Fund
              </Button>
              <Button
                variant="outline"
                onClick={() => setLocation('/conditions')}
                data-testid="button-browse-other-conditions"
              >
                Browse Other Conditions
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {wallets.map(wallet => (
              <WalletCard key={wallet.id} wallet={wallet} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
